import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Plus, Pencil, Trash2, Download, Loader2, ExternalLink, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, Spinner, EthicsNote } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { api, brl, formatErr, NICHES } from "../lib/api";

const FIELDS = [
  { name: "name", label: "Nome do fornecedor / loja", required: true },
  { name: "shop_url", label: "Link da loja na Shopee", type: "url", placeholder: "https://shopee.com.br/shop/123456789" },
  { name: "shop_id", label: "ID da loja Shopee (preenchido automaticamente pelo link)", placeholder: "123456789" },
  { name: "contact", label: "Contato", half: true },
  { name: "whatsapp", label: "WhatsApp", half: true },
  { name: "notes", label: "Observações", type: "textarea", rows: 2 },
];

const ImportDialog = ({ supplier, onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [kw, setKw] = useState("");
  const [sel, setSel] = useState({});
  const [niche, setNiche] = useState("Outros");
  const [busy, setBusy] = useState("");

  const load = async (p = 1) => {
    setBusy("load"); setErr("");
    try { const { data: d } = await api.post(`/integrations/shopee/supplier/${supplier.id}/products`, { page: p, keyword: kw }); setData(d); setPage(p); setSel({}); }
    catch (e) { setErr(formatErr(e)); setData(null); } finally { setBusy(""); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (supplier) { setKw(""); load(1); } }, [supplier]);

  const available = useMemo(() => (data?.nodes || []).filter((n) => !n.imported), [data]);
  const chosen = available.filter((n) => sel[n.itemId]);
  const allOn = available.length > 0 && chosen.length === available.length;
  const toggleAll = () => setSel(allOn ? {} : Object.fromEntries(available.map((n) => [n.itemId, true])));

  const doImport = async (items) => {
    setBusy("import");
    try {
      const { data: d } = await api.post(`/integrations/shopee/supplier/${supplier.id}/import`, { items, niche });
      d.limit_reached ? toast.warning(d.message) : toast.success(d.message);
      onDone(); load(page);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  return (
    <Dialog open={!!supplier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="supplier-import-dialog">
        <DialogHeader><DialogTitle className="font-display">Produtos da loja • {supplier?.name}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); load(1); }} className="flex gap-2">
          <Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Filtrar por palavra-chave (opcional)" className="bg-zinc-900 border-zinc-800" data-testid="supplier-import-keyword" />
          <Button type="submit" variant="outline" className="rounded-full border-zinc-700" disabled={!!busy}><Search className="h-4 w-4" /></Button>
        </form>
        {busy === "load" && <Spinner />}
        {err && (
          <div className="text-sm text-rose-400" data-testid="supplier-import-error">{err}{" "}
            {err.includes("Configure") && <Link to="/integracoes" className="text-amber-400 underline">Configurar Shopee Afiliados</Link>}
          </div>
        )}
        {data && busy !== "load" && (
          <>
            {data.nodes.length === 0 ? <p className="text-sm text-zinc-500">Nenhuma oferta encontrada para esta loja no programa de afiliados.</p> : (
              <>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-amber-500" checked={allOn} onChange={toggleAll} data-testid="supplier-import-select-all" />Selecionar todos ({available.length})</label>
                  <select className="field w-40 h-9" value={niche} onChange={(e) => setNiche(e.target.value)} data-testid="supplier-import-niche">{NICHES.map((n) => <option key={n}>{n}</option>)}</select>
                </div>
                <div className="grid gap-2">
                  {data.nodes.map((it) => (
                    <label key={it.itemId} className={`flex gap-3 items-center card-lux p-2 ${it.imported ? "opacity-50" : "cursor-pointer"}`} data-testid={`supplier-offer-${it.itemId}`}>
                      {it.imported ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> :
                        <input type="checkbox" className="h-4 w-4 accent-amber-500 shrink-0" checked={!!sel[it.itemId]} onChange={() => setSel((s) => ({ ...s, [it.itemId]: !s[it.itemId] }))} />}
                      {it.imageUrl && <img src={it.imageUrl} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0"><div className="text-sm truncate">{it.productName}</div>
                        <div className="text-xs text-zinc-400">{brl(it.priceMin)} • comissão {(Number(it.commissionRate || 0) * 100).toFixed(1)}% {it.imported && "• já cadastrado"}</div></div>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <Button size="sm" variant="ghost" disabled={page <= 1 || !!busy} onClick={() => load(page - 1)}>Anterior</Button>
                  <span>Página {page}</span>
                  <Button size="sm" variant="ghost" disabled={!data.pageInfo?.hasNextPage || !!busy} onClick={() => load(page + 1)}>Próxima</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="rounded-full border-zinc-700" disabled={!available.length || !!busy} onClick={() => doImport(available)} data-testid="supplier-import-all-button">Importar todos</Button>
                  <Button className="btn-gold rounded-full" disabled={!chosen.length || !!busy} onClick={() => doImport(chosen)} data-testid="supplier-import-selected-button">
                    {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}Importar selecionados ({chosen.length})
                  </Button>
                </div>
              </>
            )}
          </>
        )}
        <p className="text-[11px] text-zinc-500">Ofertas obtidas pela Shopee Affiliate Open API oficial, com as suas credenciais. Os links importados já são links de afiliado.</p>
      </DialogContent>
    </Dialog>
  );
};

export default function Suppliers() {
  const r = useResource("suppliers");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [imp, setImp] = useState(null);
  const [counts, setCounts] = useState({});
  const loadCounts = () => api.get("/products").then((x) => setCounts(x.data.reduce((m, p) => ({ ...m, [p.supplier_id]: (m[p.supplier_id] || 0) + 1 }), {})));
  useEffect(() => { loadCounts(); }, []);

  return (
    <div>
      <PageHeader eyebrow="Lojas parceiras" title="Fornecedores" desc="Cadastre as lojas da Shopee e importe os produtos delas direto para o seu catálogo."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg({ open: true, item: null })} data-testid="supplier-add-button"><Plus className="h-4 w-4 mr-1" />Novo fornecedor</Button>} />
      {r.loading ? <Spinner /> : r.items.length === 0 ? (
        <EmptyState icon={Store} title="Nenhum fornecedor cadastrado" desc="Adicione uma loja Shopee pelo link (ex.: shopee.com.br/shop/123456) para importar os produtos." />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {r.items.map((s) => (
            <div key={s.id} className="card-lux p-5 flex flex-col rise" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <span className="h-11 w-11 rounded-xl bg-[#EE4D2D]/15 grid place-items-center shrink-0"><Store className="h-5 w-5 text-[#ff7a5c]" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold truncate">{s.name}</div>
                  <div className="text-[11px] text-zinc-500">{s.shop_id ? `ID da loja: ${s.shop_id}` : <span className="text-amber-400">Sem ID da loja — edite para importar</span>}</div>
                </div>
                {s.shop_url && <a href={s.shop_url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
              </div>
              <div className="flex gap-4 mt-4 text-xs text-zinc-400">
                <span><b className="text-white" data-testid={`supplier-count-${s.id}`}>{counts[s.id] || 0}</b> produtos</span>
                {s.whatsapp && <span>WhatsApp: {s.whatsapp}</span>}
              </div>
              {s.notes && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{s.notes}</p>}
              <div className="mt-auto pt-4 flex gap-2">
                <Button className="btn-gold rounded-full flex-1" disabled={!s.shop_id} onClick={() => setImp(s)} data-testid={`supplier-import-${s.id}`}><Download className="h-4 w-4 mr-1" />Importar produtos</Button>
                <button className="p-2 text-zinc-500 hover:text-amber-400" onClick={() => setDlg({ open: true, item: s })} data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
                <button className="p-2 text-zinc-500 hover:text-rose-400" onClick={() => r.remove(s.id)} data-testid={`supplier-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6"><EthicsNote>A importação usa somente a API oficial de Afiliados da Shopee (sem scraping). Só aparecem produtos da loja que participam do programa de afiliados.</EthicsNote></div>
      <FormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={dlg.item ? "Editar fornecedor" : "Novo fornecedor"} fields={FIELDS}
        initial={dlg.item} testid="supplier-form" onSubmit={(b) => r.save(b, dlg.item?.id)} />
      <ImportDialog supplier={imp} onClose={() => setImp(null)} onDone={loadCounts} />
    </div>
  );
}
